/**
 * UI Property Keys for configurable modal text
 * Uses namespaced keys with pattern: ui.{feature}.{key}
 * These keys are used in the properties map of promotions and products
 *
 * Markdown Support:
 * All text fields support basic markdown for formatting:
 * - Links: [text](url) - opens in new tab
 * - Bold: **text** or __text__
 * - Italic: *text* or _text_
 * - Inline code: `text`
 *
 * Example: "Visit [our website](https://dupr.com) for **more info**"
 */

// Promotion-level property keys (affect modal header/footer)
export const UI_PROPERTY_KEYS = {
  REDEEM: {
    TITLE: 'ui.redeem.title',
    INSTRUCTIONS: 'ui.redeem.instructions',
    BENEFITS: 'ui.redeem.benefits', // Comma-separated list
    FOOTER_TEXT: 'ui.redeem.footer_text',
  },
  PRODUCT: {
    DISPLAY_NAME: 'ui.product.display_name',
    HIDE_BENEFACTOR: 'ui.product.hide_benefactor',
  },
  ENTITLEMENTS: {
    PROVIDES: 'entitlements.provides', // Comma-separated list of group.ENTITLEMENT pairs
  },
} as const

// Promotion override keys projected from the v2 promotion definition.
// These are flat keys (not under the `ui.*` namespace) surfaced by the backend
// in the promotion `properties` map — see SubscriptionController.promotionOverrideProperties.
export const PROMOTION_OVERRIDE_KEYS = {
  RC_OFFERING_ID: 'rc_offering_id', // RevenueCat offering id override for the promotion
} as const

// Type for parsed entitlement with group
export interface GroupedEntitlement {
  group: string
  entitlement: string
}

// Default values (extracted from current hard-coded text in ModalHeader.tsx)
export const UI_DEFAULTS = {
  REDEEM: {
    TITLE: 'You are eligible for a promotional discount on DUPR+!',
    INSTRUCTIONS: 'Connect with DUPR. Click on the button below. You will be directed to a checkout page.',
    BENEFITS: ['Annual Member Benefits', 'Access to exclusive features', 'Verified player badge', 'Unlimited profile views'],
  },
} as const

// Parsed promotion config interface
export interface PromotionConfig {
  title: string
  instructions: string
  benefits: string[]
  footerText?: string
  entitlementsProvides?: GroupedEntitlement[]
}

/**
 * Parse comma-separated entitlement string into GroupedEntitlement array
 * Format: "group.ENTITLEMENT,group2.ENTITLEMENT2"
 * Invalid entries (missing dot, empty group/entitlement) are logged and skipped
 */
export function parseEntitlementsProvides(value: string | undefined): GroupedEntitlement[] | undefined {
  if (!value) return undefined

  const entries = value.split(',').map((e) => e.trim()).filter((e) => e.length > 0)
  const parsed: GroupedEntitlement[] = []

  for (const entry of entries) {
    const dotIndex = entry.indexOf('.')
    if (dotIndex === -1 || dotIndex === 0 || dotIndex === entry.length - 1) {
      console.warn(`Invalid entitlement format: "${entry}". Expected "group.ENTITLEMENT"`)
      continue
    }
    parsed.push({
      group: entry.substring(0, dotIndex),
      entitlement: entry.substring(dotIndex + 1),
    })
  }

  return parsed.length > 0 ? parsed : undefined
}

/**
 * Parse promotion properties into a PromotionConfig object
 * Falls back to defaults for any missing properties
 */
export function parsePromotionConfig(properties: Record<string, string> | undefined): PromotionConfig {
  if (!properties) {
    return {
      title: UI_DEFAULTS.REDEEM.TITLE,
      instructions: UI_DEFAULTS.REDEEM.INSTRUCTIONS,
      benefits: [...UI_DEFAULTS.REDEEM.BENEFITS],
    }
  }

  const benefitsStr = properties[UI_PROPERTY_KEYS.REDEEM.BENEFITS]
  const benefits = benefitsStr
    ? benefitsStr
        .split(',')
        .map((b) => b.trim())
        .filter((b) => b.length > 0)
    : [...UI_DEFAULTS.REDEEM.BENEFITS]

  return {
    title: properties[UI_PROPERTY_KEYS.REDEEM.TITLE] || UI_DEFAULTS.REDEEM.TITLE,
    instructions: properties[UI_PROPERTY_KEYS.REDEEM.INSTRUCTIONS] || UI_DEFAULTS.REDEEM.INSTRUCTIONS,
    benefits,
    footerText: properties[UI_PROPERTY_KEYS.REDEEM.FOOTER_TEXT],
    entitlementsProvides: parseEntitlementsProvides(properties[UI_PROPERTY_KEYS.ENTITLEMENTS.PROVIDES]),
  }
}

/**
 * Get product display name from properties, falling back to the provided default
 */
export function getProductDisplayName(properties: Record<string, string> | undefined, defaultName: string): string {
  return properties?.[UI_PROPERTY_KEYS.PRODUCT.DISPLAY_NAME] || defaultName
}

/**
 * Check if a promotion config contains a reset entitlement
 * Used to detect "reset" products and render a dedicated page
 */
export function isResetPromotion(config: PromotionConfig | null | undefined): boolean {
  if (!config?.entitlementsProvides) return false
  return config.entitlementsProvides.some(
    e => e.group === 'benefit' && e.entitlement === 'RESET_MARCH_2026'
  )
}

/**
 * Check if a promotion config contains a reset extension entitlement.
 * The extension is sold separately and only offered to users who already
 * own the original RESET_MARCH_2026 entitlement.
 */
export function isResetExtensionPromotion(config: PromotionConfig | null | undefined): boolean {
  if (!config?.entitlementsProvides) return false
  return config.entitlementsProvides.some(
    (e) => e.group === 'benefit' && e.entitlement === 'RESET_MARCH_2026_EXTENSION'
  )
}
